import {
	faArrowDown,
	faArrowsUpDown,
	faArrowUp,
	faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import css from "./Autocomplete.module.css";

interface Autocomplete {
	value: string;
	options: string[];
	onChange: (value: string) => void;
}

interface ListProps {
	items: string[];
	searchTerm: string;
	loading: boolean;
	handleSelect: (item: string) => void;
	activeIndex: number;
}

export function Autocomplete({ value, options, onChange }: Autocomplete) {
	const { t } = useTranslation();
	const [sort, setSort] = useState<"default" | "asc" | "desc">("default");
	const [searchTerm, setSearchTerm] = useState(value);
	const [activeIndex, setActiveIndex] = useState(-1);
	const [open, setOpen] = useState(false);

	const filteredList =
		searchTerm == ""
			? options
			: options.filter((item) => {
					return item.toLowerCase().includes(searchTerm?.toLowerCase());
				});

	const items = useMemo(() => {
		switch (sort) {
			case "default":
				return filteredList;
			case "asc":
				return filteredList.toSorted();
			case "desc":
				return filteredList.toSorted().reverse();
		}
	}, [sort, filteredList]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setOpen(true);
		setSearchTerm(event.target.value);
	};

	const handleSelect = (item: string) => {
		setOpen(false);
		setSearchTerm(item);
		onChange(item);
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "ArrowDown") {
			setActiveIndex((prev) => (prev + 1) % filteredList.length);
		}
		if (event.key === "ArrowUp") {
			setActiveIndex(
				(prev) => (prev - 1 + filteredList.length) % filteredList.length,
			);
		}
		if (event.key === "Enter") {
			setSearchTerm(filteredList[activeIndex]);
		}
	};

	const sortIcon = () => {
		switch (sort) {
			case "default":
				return <FontAwesomeIcon icon={faArrowsUpDown} />;
			case "asc":
				return <FontAwesomeIcon icon={faArrowDown} />;
			case "desc":
				return <FontAwesomeIcon icon={faArrowUp} />;
		}
	};

	return (
		<div className={css.inputContainer}>
			<input
				id="autocomplete-input"
				autoComplete="off"
				className={css.inputAutocomplete}
				onClick={() => {
					setOpen(!open);
				}}
				onChange={handleChange}
				onKeyDown={onKeyDown}
				value={searchTerm}
			/>

			{searchTerm != "" && (
				<FontAwesomeIcon
					className={css.inputContainerIcon}
					icon={faXmark}
					onClick={() => {
						setSearchTerm("");
					}}
				/>
			)}

			{open && (
				<div className={css.divContainer}>
					<button
						className={css.sortButton}
						onClick={() => {
							switch (sort) {
								case "default":
									setSort("asc");
									break;
								case "asc":
									setSort("desc");
									break;
								case "desc":
									setSort("default");
									break;
							}
						}}
					>
						{t("sort")} {sortIcon()}
					</button>
					<List
						items={filteredList}
						searchTerm={searchTerm}
						loading={false}
						handleSelect={handleSelect}
						activeIndex={activeIndex}
					/>
				</div>
			)}
		</div>
	);
}

function List({
	items,
	searchTerm,
	loading,
	handleSelect,
	activeIndex,
}: ListProps) {
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Enter") {
				event.preventDefault();
				console.log("filteredList", items);
				console.log("activeIndex", activeIndex);
				handleSelect(items[activeIndex ?? 0]);
			}
		};
		window.addEventListener("keydown", handleKeyDown);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [items, activeIndex]);

	if (loading) {
		return <div>Loading...</div>;
	}

	if (items.length === 0) {
		return <div>No results found</div>;
	}

	return (
		<div className={css.divList}>
			{items.map((result, index) => (
				<li
					key={result}
					onClick={() => handleSelect(result)}
					className={classNames(css.listItem, {
						[css.listItemActive]: activeIndex == index,
					})}
				>
					{result}
				</li>
			))}
		</div>
	);
}
